import { surfaceBase } from "@web/components/ui";
import { useState } from "react";
import { deleteAccount } from "@/features/settings/api";

interface Props {
  onDeleted: () => void;
  hasPassword: boolean;
}

export default function DangerSection({ onDeleted, hasPassword }: Props) {
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const canDelete = confirmText === "DELETE" && (!hasPassword || !!password);

  async function handleDelete() {
    if (!canDelete) return;
    setDeleting(true);
    setError("");
    const { error: err } = await deleteAccount(hasPassword ? { password } : {});
    if (err) {
      setError(err.message);
      setDeleting(false);
    } else {
      onDeleted();
    }
  }

  function handleClose() {
    setShowModal(false);
    setConfirmText("");
    setPassword("");
    setError("");
  }

  return (
    <>
      <div className={`${surfaceBase} bg-[#fff0f0] border-[rgba(255,0,0,0.2)] p-5`}>
        <span className="text-[#cc0000] text-[1.125rem] font-black tracking-[-0.0187em]">
          Danger Zone
        </span>
        <p className="text-[#cc0000] text-[0.8125rem] mt-2 leading-5">
          Once you delete your account, there is no going back. All your data will be permanently
          removed.
        </p>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="mt-4 bg-[#cc0000] rounded-[16px] py-2.5 px-5 text-white text-[0.875rem] font-extrabold border border-[#990000] cursor-pointer hover:bg-[#aa0000] transition-colors"
        >
          Delete my account
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.5)] px-5">
          <div className={`${surfaceBase} bg-white p-6 max-w-sm w-full`}>
            <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
              Delete account
            </span>
            <p className="text-text-muted text-[0.8125rem] mt-2 leading-5">
              This action is permanent. All your data will be deleted, including competitions,
              friendships, and step history.
            </p>
            <div className="flex flex-col gap-3 mt-5">
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="confirm-delete"
                  className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                >
                  Type DELETE to confirm
                </label>
                <input
                  id="confirm-delete"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder='Type "DELETE"'
                  className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                />
              </div>
              {hasPassword && (
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="delete-password"
                    className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                  >
                    Current password
                  </label>
                  <input
                    id="delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                  />
                </div>
              )}
            </div>
            {error && <p className="text-[#cc0000] text-[0.8125rem] mt-3 font-bold">{error}</p>}
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={handleClose}
                disabled={deleting}
                className="flex-1 rounded-[16px] py-2.5 px-5 text-text text-[0.875rem] font-extrabold border border-[rgba(28,27,46,0.2)] bg-white cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canDelete || deleting}
                className="flex-1 bg-[#cc0000] rounded-[16px] py-2.5 px-5 text-white text-[0.875rem] font-extrabold border border-[#990000] cursor-pointer disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
